import { ChevronRight } from "lucide-react";
import { Fragment, useLayoutEffect, useRef, useState } from "react";

import { TableRow } from "@/components/ui/table";
import type { ColumnConfig } from "@/components/units/Tables/type";
import { cn } from "@/lib/utils";
import { commonIconProps16 } from "@/utils/constants";
import { isNotLastItem } from "@/utils/isNotLastItem";

type ExpandableRowProps<T> = {
  row: T & { children?: T[] };
  columns: ColumnConfig<T>[];
  level?: number;
  data?: T[];
  index?: number;
};

const INDENT_PER_LEVEL = 20;
const EXTRA_INDENT_FACTOR = 1.2;
const ROOT_LINE_OFFSET = 15;

function ExpandableRow<T>({
  row,
  columns,
  level = 0,
  data = [],
  index = 0,
}: ExpandableRowProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const [rowHeight, setRowHeight] = useState(0);
  const rowRef = useRef<HTMLTableRowElement>(null);

  const toggleExpand = () => setExpanded(prev => !prev);

  const isNotLast = isNotLastItem(index, data);

  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      setRowHeight(el.clientHeight);
    });
    return () => cancelAnimationFrame(raf);
  }, [expanded, row]);

  return (
    <>
      <TableRow
        ref={rowRef}
        key={row.id}
        className={cn(
          level > 0 &&
            "bg-[#F1F5F9] hover:bg-[#F1F5F9]! data-[state=selected]:bg-[#F1F5F9]!",
          !row.children?.length &&
            level > 1 &&
            "bg-[#E5ECF3] hover:bg-[#E5ECF3]! data-[state=selected]:bg-[#E5ECF3]!"
        )}
      >
        {columns
          .filter(col => col.visible)
          .map(col => (
            <Fragment key={String(col.key)}>
              {col.key === "name" ? (
                <td
                  className={cn("relative flex items-center gap-2")}
                  style={{
                    paddingLeft:
                      !row.children?.length && level > 0
                        ? (level + 1) * INDENT_PER_LEVEL
                        : level * INDENT_PER_LEVEL,
                  }}
                >
                  {row.children && row.children.length > 0 && (
                    <>
                      <button onClick={toggleExpand} className="absolute px-4">
                        <ChevronRight
                          {...commonIconProps16}
                          className={`transition-transform ${expanded ? "rotate-90" : "rotate-0"}`}
                        />

                        {expanded && (
                          <span
                            className={cn(
                              "absolute top-8 left-6 bottom-0 w-px bg-[#CBD5E1] z-[100]",
                              level === 0 && "top-10"
                            )}
                            style={{
                              height:
                                row.children.length * rowHeight -
                                (level === 0 ? ROOT_LINE_OFFSET : 0),
                            }}
                          />
                        )}
                      </button>
                    </>
                  )}

                  {!row.children?.length && level > 0 && (
                    <span
                      className={cn(
                        "absolute top-0 h-full left-0 bottom-0 bg-[#F1F5F9] z-[90]"
                      )}
                      style={{
                        width: (level + EXTRA_INDENT_FACTOR) * INDENT_PER_LEVEL,
                      }}
                    />
                  )}

                  {col.cellRenderer(
                    row,
                    isNotLast && !expanded,
                    level > 1 ? "border-b border-[#F1F5F9]!" : ""
                  )}
                </td>
              ) : (
                col.cellRenderer(
                  row,
                  isNotLast,
                  level > 1 ? "border-b border-[#F1F5F9]!" : ""
                )
              )}
            </Fragment>
          ))}
      </TableRow>

      {expanded &&
        row.children?.map((child, childIndex) => (
          <ExpandableRow
            key={child.id}
            row={child}
            columns={columns}
            level={level + 1}
            data={row.children}
            index={childIndex}
          />
        ))}
    </>
  );
}

export default ExpandableRow;
